import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, type JSX } from 'react';
import type { BoardCoreFragment } from '../../../generated/graphql';

// Cards are always shown in their manual `position` order — the order the
// user arranges by dragging. There is deliberately no sort control: an
// alternative sort cannot represent a hand-arranged order, and hiding it
// behind a mode made dragging conditional on the current view.
export interface BoardFilterState {
  text: string;
  ownerId: string | null;
}

interface BoardFilterBarProps {
  board: BoardCoreFragment;
  value: BoardFilterState;
  onChange: (next: BoardFilterState) => void;
}

export function BoardFilterBar({
  board,
  value,
  onChange,
}: BoardFilterBarProps): JSX.Element {
  const owners = useMemo(
    () =>
      Array.from(
        new Map(board.members.map((member) => [member._id, member])).values()
      ),
    [board.members]
  );

  return (
    <Stack
      direction="row"
      spacing={{ xs: 1, md: 2 }}
      sx={{ py: 1, alignItems: { sm: 'flex-end' } }}
    >
      <TextField
        label="Search cards"
        size="small"
        placeholder="Find a project…"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        value={value.text}
        onChange={(event) => onChange({ ...value, text: event.target.value })}
        sx={{ flex: 1, minWidth: 0, maxWidth: { md: 360 } }}
      />
      <FormControl
        size="small"
        sx={{ width: { xs: 120, md: 180 }, flexShrink: 0 }}
      >
        <InputLabel id="board-owner-filter-label">Owner</InputLabel>
        <Select
          labelId="board-owner-filter-label"
          label="Owner"
          value={value.ownerId ?? ''}
          displayEmpty
          renderValue={(id) =>
            id
              ? (owners.find((owner) => owner._id === id)?.firstName ?? 'Owner')
              : 'All owners'
          }
          onChange={(event) =>
            onChange({ ...value, ownerId: event.target.value || null })
          }
        >
          <MenuItem value="">All owners</MenuItem>
          {owners.map((owner) => {
            const name = `${owner.firstName} ${owner.lastName}`.trim();
            return (
              <MenuItem key={owner._id} value={owner._id}>
                {name || owner.email}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          ml: 'auto !important',
          pb: 1,
          display: { xs: 'none', md: 'block' },
        }}
      >
        Drag to arrange · Open a card to jump in
      </Typography>
    </Stack>
  );
}
